/**
 * Scene wind: the resolved wind, the wind-reactive profiles, and the CPU twin of the sway the
 * shaders compute (windShaders.ts), used by tests and by the editor's numbers.
 *
 * The model, per splat (scene coordinates, +Y up):
 * - A gust field travels downwind at the wind speed: value noise of (along - speed * t) / L, so a
 *   gust reaches a tree further downwind d / speed seconds later. L (the gust length) grows with
 *   the speed and never drops below four times the tallest reactive part, so a slow wind still
 *   moves whole trees, not single splats.
 * - The load on a part is a steady lean plus the gusts (gustiness), plus turbulence: smaller,
 *   faster noise along and across the wind.
 * - Each part sways at its natural frequency (profile and stiffness), with a phase that varies
 *   smoothly over the scene (neighbouring trees are out of step, one crown moves together).
 * - The bend grows with the height above the anchor (quadratic, like a cantilever), so trunks
 *   stay planted and crowns move; the splat also drops a little so the bend keeps its length.
 * - Flutter: each splat gets a phase from its position (smooth at leaf-cluster scale, with a
 *   little per-splat jitter) and moves a little, fast, on top.
 * - Branches: away from the trunk axis and higher up, branch-sized patches bob and sway a few
 *   times faster than the whole part, each with its own phase.
 * - Flags bend from their upwind edge instead of their base, in a wave that runs downwind.
 */
import type { CloudLayerData, CloudPreset, SceneWindSettings, WindProfile, WindResponse } from '../../types';

/** Wind-reactive areas the shader draws (each costs uniforms in every splat shader). */
export const MAX_WIND_AREAS = 4;
/** Seconds after which the wind clock wraps (phases repeat; keeps shader floats precise). */
export const WIND_TIME_PERIOD = 1800;

export interface WindProfileDefaults {
    label: string;
    /** Defaults for the creator's values. */
    amount: number;
    stiffness: number;
    flutter: number;
    anchor: number;
    /** Sideways bend at the top, as a share of the height, at full load and stiffness 0.5. */
    bend: number;
    /** Natural sway frequency in Hz at stiffness 0.5. */
    frequency: number;
    /** 0-1: how much the outer branches bob and sway on their own (faster than the whole part). */
    branches: number;
    /** 'bend': anchored at the base (height); 'flag': anchored at the upwind edge, waves downwind. */
    mode: 'bend' | 'flag';
}

export const WIND_PROFILES: Record<WindProfile, WindProfileDefaults> = {
    trees: { label: 'Trees', amount: 1, stiffness: 0.5, flutter: 0.4, anchor: 0.25, bend: 0.15, frequency: 0.3, branches: 1, mode: 'bend' },
    grass: { label: 'Grass / crops', amount: 1, stiffness: 0.25, flutter: 0.25, anchor: 0, bend: 0.3, frequency: 0.85, branches: 0, mode: 'bend' },
    bushes: { label: 'Bushes', amount: 1, stiffness: 0.6, flutter: 0.5, anchor: 0.1, bend: 0.1, frequency: 0.55, branches: 0.8, mode: 'bend' },
    flags: { label: 'Flags / fabric', amount: 1, stiffness: 0.2, flutter: 0.6, anchor: 0.05, bend: 0.22, frequency: 1.5, branches: 0, mode: 'flag' },
    custom: { label: 'Custom', amount: 1, stiffness: 0.5, flutter: 0.3, anchor: 0.2, bend: 0.12, frequency: 0.5, branches: 0.6, mode: 'bend' },
};

export const WIND_PROFILE_ORDER: WindProfile[] = ['trees', 'grass', 'bushes', 'flags', 'custom'];

/** Own keys only: `in` would also accept inherited names ("constructor", "toString"). */
const hasOwn = (record: object, key: string): boolean => Object.prototype.hasOwnProperty.call(record, key);

export function isWindProfile(value: unknown): value is WindProfile {
    return typeof value === 'string' && hasOwn(WIND_PROFILES, value);
}

export const DEFAULT_SCENE_WIND = { direction: 20, speed: 3, gustiness: 0.5, turbulence: 0.3 } as const;

export interface ResolvedSceneWind {
    enabled: boolean;
    /** Degrees, scene XZ (0 = +X, 90 = +Z, editor convention). */
    direction: number;
    /** Scene units per second. */
    speed: number;
    gustiness: number;
    turbulence: number;
}

const num = (value: unknown, fallback: number, lo = -Infinity, hi = Infinity): number => {
    const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    return Math.min(hi, Math.max(lo, n));
};

/** The wind with defaults applied; `enabled` is false when there is no wind field at all. */
export function resolveSceneWind(settings: SceneWindSettings | null | undefined): ResolvedSceneWind {
    const s = settings && typeof settings === 'object' ? settings : null;
    return {
        enabled: !!s && s.enabled !== false,
        direction: num(s?.direction, DEFAULT_SCENE_WIND.direction),
        speed: num(s?.speed, DEFAULT_SCENE_WIND.speed, 0, 1000),
        gustiness: num(s?.gustiness, DEFAULT_SCENE_WIND.gustiness, 0, 1),
        turbulence: num(s?.turbulence, DEFAULT_SCENE_WIND.turbulence, 0, 1),
    };
}

/** 0..1 load from the speed: a breeze (3) about 0.4, a strong wind (12) 0.75, a gale saturates. */
export function windStrength(speed: number): number {
    const v = Math.max(0, Number.isFinite(speed) ? speed : 0);
    return v / (v + 4);
}

/** The wind direction as a unit vector in runtime scene XZ (z mirrored from the editor's). */
export function windDirectionRuntime(directionDeg: number): { x: number; z: number } {
    const a = (directionDeg * Math.PI) / 180;
    return { x: Math.cos(a), z: -Math.sin(a) };
}

/** Gust length (scene units): five seconds of wind, never shorter than four times the tallest part. */
export function windGustLength(speed: number, tallestPart: number): number {
    return Math.max(Math.max(0, speed) * 5, Math.max(tallestPart, 0.01) * 4, 0.05);
}

export interface ResolvedWindResponse {
    enabled: boolean;
    profile: WindProfile;
    amount: number;
    stiffness: number;
    flutter: number;
    anchor: number;
    mode: 'bend' | 'flag';
    /** Bend at the top per unit load (share of the height or length), after amount and stiffness. */
    bend: number;
    /** Sway frequency (Hz) after stiffness. */
    frequency: number;
    /** Branch motion (0-1, the profile's). */
    branches: number;
}

/** A part's response with its profile's defaults for every value it leaves out. */
export function resolveWindResponse(response: WindResponse | null | undefined): ResolvedWindResponse {
    const r = response && typeof response === 'object' ? response : {};
    const profile: WindProfile = isWindProfile(r.profile) ? r.profile : 'trees';
    const p = WIND_PROFILES[profile];
    const amount = num(r.amount, p.amount, 0, 3);
    const stiffness = num(r.stiffness, p.stiffness, 0, 1);
    return {
        enabled: r.enabled !== false,
        profile,
        amount,
        stiffness,
        flutter: num(r.flutter, p.flutter, 0, 1),
        anchor: num(r.anchor, p.anchor, 0, 0.9),
        mode: p.mode,
        // stiffness 0 bends 5/3 of the profile's bend, 1 a third of it
        bend: p.bend * amount * ((1.25 - stiffness) / 0.75),
        // stiffer parts sway faster
        frequency: p.frequency * (0.65 + 0.7 * stiffness),
        branches: p.branches,
    };
}

/** The part's frame in scene coordinates: its base and height, and its extent along the wind. */
export interface WindPartExtent {
    baseY: number;
    height: number;
    /** Smallest projection of the part on the wind direction (its upwind edge). */
    upwind: number;
    /** Its length along the wind. */
    along: number;
    /** Its trunk / centre axis (scene XZ) and its radius around it (branches move with the distance). */
    axisX: number;
    axisZ: number;
    radius: number;
}

/** A stable phase (radians) per part, from its id. */
export function windPhaseSeed(id: string): number {
    let h = 2166136261;
    for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619) >>> 0;
    return ((h % 10000) / 10000) * Math.PI * 2;
}

/**
 * A part's four vec4 uniforms:
 * A = (base y, height, bend, mode 0 bend / 1 flag), B = (frequency, flutter, anchor, phase seed),
 * C = (upwind edge, length along the wind, axis x, axis z), D = (radius, branches, 0, 0).
 */
export function writeWindPartUniforms(
    out: { a: Float32Array; b: Float32Array; c: Float32Array; d: Float32Array },
    response: ResolvedWindResponse,
    extent: WindPartExtent,
    phaseSeed: number,
): void {
    const on = response.enabled && response.amount > 0 && extent.height > 0;
    out.a[0] = extent.baseY;
    out.a[1] = on ? Math.max(extent.height, 1e-4) : 0;
    out.a[2] = response.bend;
    out.a[3] = response.mode === 'flag' ? 1 : 0;
    out.b[0] = response.frequency;
    out.b[1] = response.flutter * Math.min(1, response.amount);
    out.b[2] = response.anchor;
    out.b[3] = phaseSeed;
    out.c[0] = extent.upwind;
    out.c[1] = Math.max(extent.along, 1e-4);
    out.c[2] = extent.axisX;
    out.c[3] = extent.axisZ;
    out.d[0] = Math.max(extent.radius, 1e-4);
    out.d[1] = response.branches * Math.min(1, response.amount);
    out.d[2] = 0;
    out.d[3] = 0;
}

/** The two global vec4 uniforms: A = (time, strength, gustiness, turbulence), B = (dir x, dir z, speed, gust length). */
export function writeWindGlobalUniforms(
    out: { a: Float32Array; b: Float32Array },
    wind: ResolvedSceneWind,
    time: number,
    gustLength: number,
): void {
    const d = windDirectionRuntime(wind.direction);
    out.a[0] = time;
    out.a[1] = wind.enabled ? windStrength(wind.speed) : 0;
    out.a[2] = wind.gustiness;
    out.a[3] = wind.turbulence;
    out.b[0] = d.x;
    out.b[1] = d.z;
    out.b[2] = wind.speed;
    out.b[3] = gustLength;
}

// ── CPU twin of windShaders.ts (tests; keep in step with WIND_LIBRARY_GLSL) ───────────────

const fract = (x: number) => x - Math.floor(x);
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

/** Dave Hoskins' hash12, as the shader (float32 differences aside). */
export function windHash(px: number, py: number): number {
    let x = fract(px * 0.1031);
    let y = fract(py * 0.1031);
    let z = fract(px * 0.1031);
    const d = x * (y + 33.33) + y * (z + 33.33) + z * (x + 33.33);
    x += d;
    y += d;
    z += d;
    return fract((x + y) * z);
}

export function windNoise(px: number, py: number): number {
    const ix = Math.floor(px), iy = Math.floor(py);
    const fx = px - ix, fy = py - iy;
    const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
    return mix(mix(windHash(ix, iy), windHash(ix + 1, iy), ux), mix(windHash(ix, iy + 1), windHash(ix + 1, iy + 1), ux), uy);
}

/** The travelling gust field (0..1) at a point, as the shader samples it. */
export function windGust(along: number, cross: number, speed: number, time: number, gustLength: number): number {
    const x = along - speed * time;
    return 0.65 * windNoise(x / gustLength, cross / (1.6 * gustLength)) + 0.35 * windNoise(x / (0.45 * gustLength) + 17, cross / (0.7 * gustLength) + 17);
}

/**
 * The offset (scene coordinates) the shader gives a splat at scene position p. `globals` and
 * `part` are the uniform values (writeWindGlobalUniforms / writeWindPartUniforms).
 */
export function windOffsetCPU(
    p: { x: number; y: number; z: number },
    globals: { a: ArrayLike<number>; b: ArrayLike<number> },
    part: { a: ArrayLike<number>; b: ArrayLike<number>; c: ArrayLike<number>; d: ArrayLike<number> },
): { x: number; y: number; z: number } {
    const [t, s, g, turbulence] = [globals.a[0], globals.a[1], globals.a[2], globals.a[3]];
    const [dx, dz, speed, L] = [globals.b[0], globals.b[1], globals.b[2], globals.b[3]];
    const [baseY, H, bend, mode] = [part.a[0], part.a[1], part.a[2], part.a[3]];
    const [freq, flutter, anchor, seed] = [part.b[0], part.b[1], part.b[2], part.b[3]];
    if (s <= 0 || H <= 0) return { x: 0, y: 0, z: 0 };
    const px = -dz, pz = dx; // perpendicular (scene XZ)
    const along = p.x * dx + p.z * dz;
    const cross = p.x * px + p.z * pz;
    const gust = windGust(along, cross, speed, t, L);
    const tu = windNoise(along / (0.6 * L) + t * 0.9 + 3, cross / (0.6 * L) - t * 0.6 + 3) - 0.5;
    const tc = windNoise(along / (0.6 * L) - t * 0.7 + 41, cross / (0.6 * L) + t * 0.8 + 41) - 0.5;
    const phase = seed + 6.2832 * windNoise(p.x / (8 * H) + 7, p.z / (8 * H) + 7);
    const w = 6.2832 * freq * t + phase;
    const load = s * (0.3 + g * (gust - 0.45) * 1.8);
    const osc = s * Math.sin(w) * (0.25 + 0.6 * g * gust);
    const fAlong = load + osc + turbulence * s * 1.2 * tu;
    const fCross = s * Math.sin(w * 1.37 + 1.3) * (0.08 + 0.15 * g * gust) + turbulence * s * tc;
    const hs = windNoise((p.x + p.y * 0.6) / (0.05 * H) + 23, (p.z - p.y * 0.4) / (0.05 * H) + 23) * 0.85 + windHash(p.x * (97 / H) + p.y * (61 / H), p.z * (97 / H) + p.y * (61 / H)) * 0.15;
    const ft = 6.2832 * t;
    let ox: number, oy: number, oz: number, k: number;
    if (mode < 0.5) {
        const h = clamp((p.y - baseY) / H, 0, 1);
        k = Math.max(0, (h - anchor) / Math.max(1 - anchor, 0.05));
        const amp = bend * H * k * k;
        const ddx = (fAlong * dx + fCross * px) * amp;
        const ddz = (fAlong * dz + fCross * pz) * amp;
        const r = Math.max(k * H * (1 - anchor), 0.05 * H);
        ox = ddx;
        oz = ddz;
        oy = (-0.5 * (ddx * ddx + ddz * ddz)) / r;
        const radial = Math.hypot(p.x - part.c[2], p.z - part.c[3]) / Math.max(part.d[0], 1e-4);
        const sm = (e0: number, e1: number, v: number) => { const q = clamp((v - e0) / (e1 - e0), 0, 1); return q * q * (3 - 2 * q); };
        const wb = sm(0.15, 1, radial) * k * part.d[1];
        if (wb > 0) {
            const bp = 6.2832 * windNoise((p.x + p.y * 0.7) / (0.3 * H) + 11, (p.z - p.y * 0.4) / (0.3 * H) + 11);
            const bw = 6.2832 * freq * 3.2 * t + bp;
            const ba = bend * H * 0.18 * s * (0.35 + 0.65 * gust) * wb;
            const side = Math.sin(bw * 0.83 + 1.7) * 0.5;
            ox += dx * side * ba;
            oy += Math.sin(bw) * ba;
            oz += dz * side * ba;
        }
    } else {
        const len = part.c[1];
        const u = clamp((along - part.c[0]) / len, 0, 1);
        k = Math.max(0, (u - anchor) / Math.max(1 - anchor, 0.05));
        const wave = Math.sin(6.2832 * (along / (0.75 * len) - freq * (0.6 + s) * t) + phase);
        const wave2 = Math.cos(6.2832 * (along / (0.5 * len) - freq * 1.3 * (0.6 + s) * t) + phase * 1.7);
        const amp = bend * len * Math.pow(k, 1.3) * s * (0.7 + 0.6 * g * gust);
        ox = px * wave * amp * 0.35 - dx * amp * 0.08;
        oz = pz * wave * amp * 0.35 - dz * amp * 0.08;
        oy = wave2 * amp * 0.15;
    }
    const fa = flutter * 0.012 * H * s * k;
    ox += fa * Math.sin(ft * (2.6 + 1.4 * hs) + 6.2832 * hs);
    oy += fa * 0.6 * Math.sin(ft * (3.1 + 1.1 * hs) + 2.1 + 6.2832 * hs);
    oz += fa * Math.cos(ft * (2.3 + 1.7 * hs) + 4.2 + 6.2832 * hs);
    return { x: ox, y: oy, z: oz };
}

// ── clouds and weather that follow the wind ───────────────────────────────────────────────

/**
 * A preset's cloud speed relative to the scene wind: its own default speed over the
 * fair-weather cumulus speed, so a sky of mixed layers keeps its relative motion.
 */
export const CLOUD_WIND_MULTIPLIERS: Record<CloudPreset, number> = {
    cumulus: 1,
    overcast: 0.75,
    cirrus: 2.5,
    storm: 1.5,
    fog: 0.3,
    stylised: 1.25,
};

export function cloudWindMultiplier(layer: Pick<CloudLayerData, 'preset' | 'windSpeedMultiplier'> | null | undefined): number {
    const preset = layer?.preset && hasOwn(CLOUD_WIND_MULTIPLIERS, layer.preset) ? layer.preset : 'cumulus';
    return num(layer?.windSpeedMultiplier, CLOUD_WIND_MULTIPLIERS[preset as CloudPreset], 0, 100);
}

/**
 * The wind a cloud layer drifts with: the scene wind (times its multiplier) while it follows the
 * wind and the scene has wind, else null (its own windDirection / windSpeed).
 */
export function cloudSceneWind(
    layer: Pick<CloudLayerData, 'preset' | 'followSceneWind' | 'windSpeedMultiplier'> | null | undefined,
    wind: ResolvedSceneWind | null | undefined,
): { windDirection: number; windSpeed: number } | null {
    if (!layer?.followSceneWind || !wind?.enabled) return null;
    return { windDirection: wind.direction, windSpeed: wind.speed * cloudWindMultiplier(layer) };
}
